import { Switch } from '@material-ui/core'
import { AccountSettingSelect } from 'components/controls/misc'
import { useUser } from 'components/providers/UserProvider'
import { editUserAttribute } from 'lib/api'
import { isFromMainHost } from 'lib/utils'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { UserSettings } from 'shared'
import { APP_LANGUAGES } from 'utils/constants'
import { ReferralPresenter } from '../dashboard/ReferralPresenter'

export const AppSettingsPresenter = (): JSX.Element => {
  const { t, i18n } = useTranslation()
  const { user, setUser } = useUser()
  const { enqueueSnackbar } = useSnackbar()
  const [activeRemindPurchaseEvent, setActiveRemindPurchaseEvent] = useState(
    user.enabledNotifications?.askLinkToPurchaseEvent ?? false
  )
  const [disableHtmlProductDescription, setDisableHtmlProductDescription] = useState(
    user.settings?.shopify?.disableHtmlProductDescription ?? false
  )
  const [setProductToDraftWhenOutOfStock, setSetProductToDraftWhenOutOfStock] = useState(
    user.settings?.shopify?.setProductToDraftWhenOutOfStock ?? false
  )
  const handleChangeReminderPurchaseEvent = async () => {
    const notifications = user.enabledNotifications ?? {}
    const response = await editUserAttribute(
      {
        ...notifications,
        askLinkToPurchaseEvent: !activeRemindPurchaseEvent,
      },
      'enabledNotifications'
    )
    setUser({ ...user, ...response })
    setActiveRemindPurchaseEvent(!activeRemindPurchaseEvent)
  }

  const updateShopifySetting = async (
    key: keyof NonNullable<UserSettings['shopify']>,
    value: boolean,
    onSuccess: () => void
  ) => {
    const nextSettings: UserSettings = {
      ...user.settings,
      shopify: { ...user.settings?.shopify, [key]: value },
    }
    try {
      const response = await editUserAttribute(nextSettings, 'settings')
      setUser({ ...user, ...response })
      onSuccess()
    } catch (e) {
      enqueueSnackbar(t('account.settings.shopify.saveError'), { variant: 'error' })
    }
  }

  const handleToggleDisableHtmlProductDescription = async () => {
    const next = !disableHtmlProductDescription
    await updateShopifySetting('disableHtmlProductDescription', next, () =>
      setDisableHtmlProductDescription(next)
    )
  }

  const handleToggleSetProductToDraftWhenOutOfStock = async () => {
    const next = !setProductToDraftWhenOutOfStock
    await updateShopifySetting('setProductToDraftWhenOutOfStock', next, () =>
      setSetProductToDraftWhenOutOfStock(next)
    )
  }

  const referral = () => {
    if (!isFromMainHost()) return <></>

    return (
      <>
        <hr style={{ marginTop: '32px', marginBottom: '24px' }} />
        <h5 className="pb-2" style={{ display: 'flex', alignItems: 'center' }}>
          <i className="fa-solid fa-gift me-2" />
          {t('signup.referral')}
        </h5>
        <ReferralPresenter />
      </>
    )
  }

  return (
    <>
      <div className="d-flex flex-row align-items-center">
        <span className="me-2">{t('common.AppliactionLanguage')}</span>
        <div style={{ width: '150px' }}>
          <AccountSettingSelect
            attribute={'language'}
            options={APP_LANGUAGES}
            onChange={(language) => i18n.changeLanguage(language as string)}
          />
        </div>
      </div>

      {user.featureFlags?.taxation && (
        <>
          <div className="d-flex flex-row align-items-center mt-4">
            <span>{t('taxation.remindLinkPeSetting')}</span>
            <Switch
              name={t('account.remindMe')}
              checked={activeRemindPurchaseEvent}
              onChange={handleChangeReminderPurchaseEvent}
            />
          </div>
          <p className="pb-3">{t('taxation.automaticLinkToPeExplanation')}</p>
        </>
      )}

      {referral()}

      {user.featureFlags?.shopify && (
        <>
          <hr style={{ marginTop: '32px', marginBottom: '24px' }} />
          <h5 className="pb-2" style={{ display: 'flex', alignItems: 'center' }}>
            <i className="fa-brands fa-shopify me-2" />
            {t('account.settings.shopify.title')}
          </h5>
          <p className="pb-2 text-muted" style={{ fontStyle: 'italic' }}>
            {t('account.settings.shopify.futureChangesOnly')}
          </p>

          <div className="d-flex flex-row align-items-center mt-2">
            <span>{t('account.settings.shopify.disableHtmlProductDescription')}</span>
            <Switch
              name="disable html product description"
              checked={disableHtmlProductDescription}
              onChange={handleToggleDisableHtmlProductDescription}
            />
          </div>
          <p className="pb-3">{t('account.settings.shopify.disableHtmlProductDescriptionHelp')}</p>

          <div className="d-flex flex-row align-items-center mt-2">
            <span>{t('account.settings.shopify.setProductToDraftWhenOutOfStock')}</span>
            <Switch
              name="set product to draft when out of stock"
              checked={setProductToDraftWhenOutOfStock}
              onChange={handleToggleSetProductToDraftWhenOutOfStock}
            />
          </div>
          <p className="pb-3">
            {t('account.settings.shopify.setProductToDraftWhenOutOfStockHelp')}
          </p>
        </>
      )}
    </>
  )
}
